import { isArray, runningClientSide } from '@benzinga/utils';
import {
  AllScannerUserSetting,
  FilterIngress,
  ScannerConfigV1,
  ScannerConfigV3,
  ScannerUserSetting,
  ScannerVersion,
} from './entities';
import { UserSettingConfig } from '@benzinga/user-settings';
import { FilterObject } from '@benzinga/quotes-v3-fields-manager';
import { DefaultTableParameters } from '@benzinga/ag-grid-utils';

export function toQueryFiltersObject(filters: (string | FilterIngress | FilterObject)[]): FilterObject[] {
  return filters.map(filter => {
    if (typeof filter === 'string') {
      const tokens = filter.split('_');
      const params = tokens.slice(2).join('_').split(',');
      return { field: tokens[0], operator: tokens[1], parameters: params };
    } else {
      return {
        ...filter,
        field: typeof filter.field === 'string' ? filter.field : filter.field.name,
        parameters: filter.parameters.map(p => (p === null ? '' : typeof p === 'number' ? p.toString() : p)),
      };
    }
  });
}

// these are v1 on purpose, newer versions will be handled within the loader
const recursiveScans = (scans: ScannerConfigV1[], version: number): ScannerConfigV1[] => {
  switch (version) {
    case 0: {
      const data = runningClientSide() ? (window.localStorage?.getItem?.('scannerLayouts') ?? '[]') : '[]';
      const scans = [...JSON.parse(data ?? '[]')];

      const toScannerConfig = (config: ScannerConfigV1): ScannerConfigV1 => {
        return {
          ...config,
          filters: toQueryFiltersObject(config.filters),
        };
      };

      const configs = scans.map(toScannerConfig);
      return recursiveScans(configs, 1);
    }
    case 1: {
      const scansV1 = [...scans];
      return recursiveScans(scansV1, 2);
    }
    case 2: {
      const filtersScans = scans.filter(scan => scan.name === 'Large-Cap Gainers, many Columns');
      if (filtersScans.length > 0) {
        const filter = filtersScans[0].filters.filter(column => column.field === 'marketCap');
        if (filter.length > 0) {
          filter[0].parameters[0] = '10000000000';
          filter[0].parameters[1] = '200000000000';
        }
      }
      return recursiveScans(scans, 3);
    }
    default: {
      return scans;
    }
  }
};

const loader = (
  setting: AllScannerUserSetting | undefined,
  allSettings: Record<string, unknown> | undefined,
): ScannerUserSetting => {
  const legacy_setting = allSettings?.['scans'] as ScannerConfigV1[] | undefined;
  const legacy_version = allSettings?.['scans_version'] as number | undefined;
  if (setting == null) {
    if (isArray(legacy_setting)) {
      return loader(
        {
          configs: recursiveScans(legacy_setting, legacy_version ?? 0),
          version: 1,
        },
        allSettings,
      );
    } else {
      return loader({ configs: [], version: 1 }, allSettings);
    }
  }

  switch (setting.version) {
    case 1:
      return loader(
        {
          ...setting,
          version: 2,
        },
        allSettings,
      );
    case 2:
      return loader(
        {
          configs: setting.configs.map<ScannerConfigV3>(config => {
            const { columns, ...rest } = config;
            return {
              ...rest,
              tableParameters: {
                ...DefaultTableParameters,
                columns: columns.map(column => {
                  return {
                    colId: column.name,
                    width: column.width,
                  };
                }),
              },
              tags: [],
            };
          }),
          version: 3,
        },
        allSettings,
      );
    case 3:
      return setting;
  }
};

export const ScannerUserSettingConfig: UserSettingConfig<
  'scanner',
  ScannerVersion,
  ScannerUserSetting,
  AllScannerUserSetting
> = {
  key: 'scanner',
  loader,
  version: 3,
};
